import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Search, Briefcase, FileText, MessageSquare, User, Target, 
  GraduationCap, FileCheck, Eye, DollarSign, MapPin, Clock,
  TrendingUp, Users, Heart, Book, Bookmark, Share2, ChevronDown,
  Upload, X
} from 'lucide-react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

export default function Careers() {
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showGeneralInquiry, setShowGeneralInquiry] = useState(false);
  const [savedJobs, setSavedJobs] = useState([]);
  const { user } = useAuth();
  const [sortBy, setSortBy] = useState('relevance');
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const navigate = useNavigate();
  const resultsRef = useRef(null);
  const testimonials = [
    {
      quote:
        "Symetrix didn't just find me a job — they found me the perfect career fit. The recruiter took time to understand my goals and matched me with a company that truly values what I bring to the table.",
      name: 'Sarah Johnson',
      role: 'Senior Software Engineer',
      company: 'Tech Innovations Inc.',
    },
    {
      quote:
        "The support I received throughout the interview process was exceptional. My recruiter prepared me thoroughly and advocated for me every step of the way. I've never felt more confident going into interviews.",
      name: 'Michael Chen',
      role: 'Product Manager',
      company: 'Digital Solutions Ltd.',
    },
    {
      quote:
        "I was skeptical about working with a recruiter, but Symetrix changed my perspective completely. They understood my industry and connected me with opportunities I would never have found on my own.",
      name: 'Emily Rodriguez',
      role: 'Marketing Director',
      company: 'Global Brands Corp.',
    },
    {
      quote:
        "Joining Symetrix was the best career decision I've made. The culture is collaborative, the work is meaningful, and the growth opportunities are endless. I've been able to work with amazing clients and develop skills I never thought possible.",
      name: 'Alex Thompson',
      role: 'Senior Talent Consultant',
      company: 'Symetrix HR Consulting',
    },

  ];

  const [ctaPulsed, setCtaPulsed] = useState(false);

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.title = searchQuery;
      if (locationFilter) params.location = locationFilter;
      if (departmentFilter) params.department = departmentFilter;
      const data = await api.getJobs(params);
      
      let filtered = data;
      if (remoteOnly) {
        filtered = data.filter(job => job.location?.toLowerCase().includes('remote'));
      }
      
      // Sort
      if (sortBy === 'newest') {
        filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      } else if (sortBy === 'location') {
        filtered.sort((a, b) => (a.location || '').localeCompare(b.location || ''));
      }
      
      setJobs(filtered);
    } catch (e) {
      toast.error(e.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, locationFilter, departmentFilter, remoteOnly, sortBy]);

  // Auto-rotate testimonials
  useEffect(() => {
    if (!testimonials.length) return;

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 8000); // 8 seconds per testimonial

    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Scroll to results and set focus after search
  useEffect(() => {
    if (jobs.length > 0 && resultsRef.current) {
      const firstCard = resultsRef.current.querySelector('[role="article"]');
      if (firstCard) {
        firstCard.focus();
      }
    }
  }, [jobs]);

  // Pulse CTA once per session
  useEffect(() => {
    const pulsed = sessionStorage.getItem('cta-pulsed');
    if (!pulsed) {
      setCtaPulsed(true);
      sessionStorage.setItem('cta-pulsed', 'true');
    }
  }, []);

  const handleSaveJob = (jobId) => {
    setSavedJobs(prev => 
      prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId]
    );
    toast.success(savedJobs.includes(jobId) ? 'Job removed from saved' : 'Job saved!');
  };

  const handleShareJob = (job) => {
    if (navigator.share) {
      navigator.share({
        title: job.title,
        text: `Check out this job: ${job.title} at ${job.company || 'Symetrix Client'}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    }
  };

  const handleApply = async (job) => {
    if (!user) {
      toast.error('Please sign in to apply for jobs');
      navigate('/login');
      return;
    }

    try {
      // Prevent duplicate client-side submissions for the same job
      if (appliedJobs.includes(job.id)) {
        toast.info('You have already applied for this job');
        return;
      }

      await api.createApplication({
        job_opening_id: job.id,
        name: user.full_name || user.email,
        email: user.email,
      });
      setAppliedJobs((prev) => [...prev, job.id]);
      toast.success('Application submitted successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to submit application');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* 1) HERO - Candidate First */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-teal-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
              data-anim="typing"
              data-speed="32"
            >
              Looking for your next opportunity? We'll help you find it.
            </h1>
            <p
              className="text-xl text-blue-50 mb-8 max-w-3xl mx-auto"
              data-anim="fade-up"
              data-delay="120"
            >
              We help you find <span
                data-anim="rotating-text"
                data-words="opportunities|your dream job|great positions|career growth|the perfect role"
                data-speed="3000"
                data-transition="500"
                className="font-semibold text-transparent bg-clip-text text-2xl bg-[linear-gradient(90deg,#fbbf24,#f59e0b,#fcd34d)] bg-[length:200%_100%] animate-[gradient-move_3s_linear_infinite]"
              >opportunities</span> that match your goals, skills and personality — across startups, scale-ups and enterprises.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-anim="scale-in" data-delay="200">
              <Button 
                size="lg" 
                className={`bg-white text-blue-600 hover:bg-blue-50 px-8 transform hover:scale-105 transition-all shadow-xl ${ctaPulsed ? 'animate-pulse' : ''}`}
                onClick={() => document.getElementById('job-list')?.scrollIntoView({behavior: 'smooth'})}
              >
                <Briefcase className="mr-2" size={20} />
                Find a Job
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 transform hover:scale-105 transition-all"
                onClick={() => setShowResumeModal(true)}
              >
                <FileText className="mr-2" size={20} />
                Submit Your Resume
              </Button>
            </div>
            
            {/* Hero Search - Transparent UI */}
            <div className="max-w-4xl mx-auto" data-anim="fade-up" data-delay="350">
              <div className="p-6 bg-white/10 backdrop-blur-md border border-white/20">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      placeholder="Search jobs by title, skill or company..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-12 bg-white/20 border-white/30 text-white placeholder:text-white/70 focus:bg-white/30 focus:border-white/50"
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Location (e.g., Remote, New York)"
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      className="h-12 bg-white/20 border-white/30 text-white placeholder:text-white/70 focus:bg-white/30 focus:border-white/50"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <Button 
                      className="w-full bg-white text-blue-600 hover:bg-white/90 h-12"
                      onClick={() => {
                        fetchJobs();
                        document.getElementById('job-list')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <Search className="mr-2" size={20} />
                      Search
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2) CURRENT OPENINGS - Client Roles */}
      <section id="job-list" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-bold text-gray-900 mb-4"
              data-anim="fade-up"
            >
              Current Openings
            </h2>
            <p 
              className="text-lg text-gray-600"
              data-anim="fade-up"
              data-delay="100"
            >
              Explore leadership, specialist, contract, remote and permanent roles across industries.
            </p>
          </div>
          
          {/* Filters and Sorting */}
          <div className="mb-8 flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex gap-4 flex-wrap">
              <select 
                className="pl-4 pr-10 py-2 border bg-white appearance-none bg-no-repeat bg-right"
                style={{backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.5rem center", backgroundSize: "1.5em 1.5em"}}
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <option value="">All Departments</option>
                <option value="Engineering">Engineering</option>
                <option value="Sales">Sales</option>
                <option value="Marketing">Marketing</option>
                <option value="Operations">Operations</option>
              </select>
              
              <label className="flex items-center gap-2 px-4 py-2 bg-white border cursor-pointer">
                <Checkbox 
                  checked={remoteOnly}
                  onCheckedChange={setRemoteOnly}
                />
                <span className="text-sm">Remote only</span>
              </label>
            </div>
            
            <select 
              className="pl-4 pr-10 py-2 border bg-white appearance-none bg-no-repeat bg-right"
              style={{backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.5rem center", backgroundSize: "1.5em 1.5em"}}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="relevance">Sort by: Relevance</option>
              <option value="newest">Newest</option>
              <option value="location">Location</option>
            </select>
          </div>
          
          {/* Job List */}
          <div 
            ref={resultsRef}
            className="space-y-6"
            aria-live="polite"
            role="region"
            aria-label="Job search results"
          >
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Loading opportunities...</p>
              </div>
            ) : jobs.length > 0 ? (
              jobs.map((job) => (
                <article 
                  key={job.id}
                  role="article"
                  aria-labelledby={`job-title-${job.id}`}
                  tabIndex={0}
                  className="bg-white p-6 shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <h3 id={`job-title-${job.id}`} className="text-xl font-semibold text-gray-900 mb-2">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-3">
                        <span className="flex items-center gap-1">
                          <Briefcase size={16} />
                          {job.company || 'Symetrix Client'}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin size={16} />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={16} />
                          {job.type || 'Full-time'}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">{job.description?.slice(0, 150)}...</p>
                      <div className="flex gap-2 flex-wrap">
                        {job.location?.toLowerCase().includes('remote') && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs">Remote</span>
                        )}
                        {job.department && (
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs">{job.department}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex md:flex-col gap-2">
                      <Button 
                        className={`text-white ${
                          appliedJobs.includes(job.id)
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                        onClick={() => handleApply(job)}
                        aria-label={
                          appliedJobs.includes(job.id)
                            ? `Already applied for ${job.title}`
                            : `Apply for ${job.title}`
                        }
                        disabled={appliedJobs.includes(job.id)}
                      >
                        {appliedJobs.includes(job.id) ? 'Applied' : 'Apply'}
                      </Button>
                      <Button 
                        variant="outline"
                        size="icon"
                        onClick={() => handleSaveJob(job.id)}
                        aria-label={savedJobs.includes(job.id) ? 'Remove from saved' : 'Save job'}
                      >
                        <Bookmark className={savedJobs.includes(job.id) ? 'fill-current' : ''} size={20} />
                      </Button>
                      <Button 
                        variant="outline"
                        size="icon"
                        onClick={() => handleShareJob(job)}
                        aria-label="Share job"
                      >
                        <Share2 size={20} />
                      </Button>
                    </div>
                  </div>
                </article>
              ))
            ) : (
              // 5) EMPTY STATE
              <div className="text-center py-16 bg-white">
                <div className="max-w-2xl mx-auto">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">No jobs found matching your criteria.</h3>
                  <p className="text-gray-600 mb-8">
                    Try broadening your search (remove location or use fewer keywords), or:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <Card className="hover:shadow-lg transition-all cursor-pointer" onClick={() => setShowResumeModal(true)}>
                      <CardContent className="p-6 text-center">
                        <FileText className="mx-auto text-blue-600 mb-3" size={32} />
                        <h4 className="font-semibold mb-2">Submit your resume</h4>
                        <p className="text-sm text-gray-600">We'll match you as roles open.</p>
                      </CardContent>
                    </Card>
                    
                    <Card
                      className="hover:shadow-lg transition-all cursor-pointer"
                      onClick={async () => {
                        try {
                          const email = window.prompt('Enter your email to get job alerts:');
                          if (!email) return;
                          await api.createJobAlert({
                            email,
                            keywords: searchQuery || undefined,
                            location: locationFilter || undefined,
                          });
                          toast.success('Job alert created successfully.');
                        } catch (err) {
                          toast.error(err.message || 'Failed to create job alert');
                        }
                      }}
                    >
                      <CardContent className="p-6 text-center">
                        <Briefcase className="mx-auto text-green-600 mb-3" size={32} />
                        <h4 className="font-semibold mb-2">Create job alert</h4>
                        <p className="text-sm text-gray-600">Get new matches by email.</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="hover:shadow-lg transition-all cursor-pointer" onClick={() => setShowGeneralInquiry(true)}>
                      <CardContent className="p-6 text-center">
                        <MessageSquare className="mx-auto text-teal-600 mb-3" size={32} />
                        <h4 className="font-semibold mb-2">Talk to a recruiter</h4>
                        <p className="text-sm text-gray-600">Submit a general inquiry.</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="text-sm text-gray-600">Try searching:</span>
                    {['Engineering', 'Sales', 'Marketing', 'Operations', 'Remote'].map(dept => (
                      <button
                        key={dept}
                        className="px-3 py-1 bg-blue-100 text-blue-700 text-sm hover:bg-blue-200 transition-colors"
                        onClick={() => setDepartmentFilter(dept)}
                      >
                        {dept}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 3) SUPPORT AT EVERY STAGE */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-bold text-gray-900 mb-4"
              data-anim="fade-up"
            >
              Support at every stage of your career
            </h2>
            <p 
              className="text-lg text-gray-600 max-w-3xl mx-auto"
              data-anim="fade-up"
              data-delay="100"
            >
              From resume polish to interview coaching to access to unadvertised roles, Symetrix is with you every step.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-anim="list-stagger" data-stagger="80">
            {[
              { icon: User, title: 'Personal recruiter', description: 'One recruiter who knows your skills and goals.' },
              { icon: Target, title: 'Tailored job matching', description: 'We focus on jobs that suit your ambitions.' },
              { icon: GraduationCap, title: 'Interview preparation', description: 'Role-play, feedback and practical coaching.' },
              { icon: FileCheck, title: 'Resume & profile help', description: 'Make your CV and LinkedIn stand out.' },
              { icon: Eye, title: 'Access hidden jobs', description: "Many openings aren't posted publicly — we'll connect you." },
              { icon: DollarSign, title: 'Free for candidates', description: 'Working with us costs you nothing.' }
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card 
                  key={index} 
                  className="hover:shadow-lg transition-all border-none"
                >
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-blue-100 flex items-center justify-center mb-4">
                      <IconComponent className="text-blue-600" size={24} />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4) DON'T SEE THE RIGHT ROLE */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Don't see the role you want? Let's talk.</h2>
          <p className="text-lg text-gray-600 mb-8">
            Submit your resume or send a quick message — we'll identify roles that match your ambitions, even if they're not posted.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              onClick={() => setShowResumeModal(true)}
            >
              Submit your resume
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8"
              onClick={() => setShowGeneralInquiry(true)}
            >
              Get career advice
            </Button>
          </div>
        </div>
      </section>

      {/* ROTATING TESTIMONIALS */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Candidates Say</h2>
            <p className="text-lg text-gray-600">Hear from professionals who found their dream careers through Symetrix</p>
          </div>
          
          <div className="relative" data-anim="fade-up" data-delay="150">
            {/* Testimonial Carousel */}
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}
              >
                {testimonials.map((testimonial, idx) => (
                  <div key={idx} className="w-full flex-shrink-0 px-4">
                    <Card className="border-none shadow-xl bg-gradient-to-br from-gray-50 to-white">
                      <CardContent className="p-10 text-center">
                        <div className="mb-6">
                          <svg
                            className="w-12 h-12 text-blue-200 mx-auto"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                          </svg>
                        </div>
                        <p className="text-xl text-gray-700 mb-8 italic leading-relaxed">
                          "{testimonial.quote}"
                        </p>
                        <div className="flex items-center justify-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                            {testimonial.name.charAt(0)}
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-gray-900">{testimonial.name}</p>
                            <p className="text-sm text-gray-600">{testimonial.role}</p>
                            <p className="text-xs text-gray-500">{testimonial.company}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentTestimonial(idx)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    currentTestimonial === idx
                      ? 'bg-blue-600 w-8'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to testimonial ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 8) CAREERS AT SYMETRIX - Internal */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-bold text-white mb-4"
              data-anim="fade-up"
            >
              Build Your Career with Symetrix
            </h2>
            <p 
              className="text-xl text-blue-100"
              data-anim="fade-up"
              data-delay="100"
            >
              Work with driven professionals shaping the future of talent.
            </p>
          </div>
          
          <Card className="mb-8 border-l-4 border-l-white bg-white/95 backdrop-blur">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Work at Symetrix</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <TrendingUp className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-900">Career Growth</p>
                    <p className="text-gray-600">Structured development paths, mentorship and clear progression.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Users className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-900">Collaborative Culture</p>
                    <p className="text-gray-600">Team-focused work and open communication.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="text-pink-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-900">Comprehensive Benefits</p>
                    <p className="text-gray-600">Health insurance and flexible leave.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Book className="text-orange-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-900">Learning & Development</p>
                    <p className="text-gray-600">Training and internal knowledge sharing.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-gray-900">Flexible Work Options</p>
                    <p className="text-gray-600">Hybrid and remote roles where possible.</p>
                  </div>
                </li>
              </ul>
              
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button className="bg-green-600 hover:bg-green-700 text-white">
                  View Symetrix Openings
                </Button>
                <Button variant="outline" onClick={() => setShowResumeModal(true)}>
                  Submit Your Resume
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-bold text-gray-900 mb-4"
              data-anim="fade-up"
            >
              What Our Candidates Say
            </h2>
            <p 
              className="text-lg text-gray-600"
              data-anim="fade-up"
              data-delay="100"
            >
              Real stories from professionals we've helped advance their careers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-anim="list-stagger" data-stagger="120">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="text-blue-600 mb-4">
                  <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 32 32">
                    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                  </svg>
                </div>
                <p className="text-gray-600 mb-6 italic">
                  "Symetrix helped me land my dream role at a top tech company. Their personalized approach and industry insights were invaluable throughout the process."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold mr-4">
                    RM
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Rachel Martinez</p>
                    <p className="text-sm text-gray-500">Senior Software Engineer</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="text-green-600 mb-4">
                  <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 32 32">
                    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                  </svg>
                </div>
                <p className="text-gray-600 mb-6 italic">
                  "The team at Symetrix understood exactly what I was looking for. They matched me with a company that aligned perfectly with my values and career goals."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold mr-4">
                    JK
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">James Kim</p>
                    <p className="text-sm text-gray-500">Product Manager</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="text-teal-600 mb-4">
                  <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 32 32">
                    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                  </svg>
                </div>
                <p className="text-gray-600 mb-6 italic">
                  "Professional, responsive, and genuinely invested in my success. Symetrix made my job search stress-free and helped me negotiate an excellent package."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 font-bold mr-4">
                    AP
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Alicia Patel</p>
                    <p className="text-sm text-gray-500">Financial Analyst</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 9) FAQ */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[
              { q: 'Do I need to pay to work with Symetrix?', a: 'No. Our services are completely free for job seekers.' },
              { q: 'How long does the process take?', a: 'It varies by role, but we typically respond within 48 hours and aim to complete placements within 2-4 weeks.' },
              { q: 'Can I apply for multiple roles?', a: 'Yes! Feel free to apply for any roles that match your skills and interests.' },
              { q: 'What if I don\'t see a role that fits?', a: 'Submit your resume and we\'ll reach out when matching opportunities arise.' }
            ].map((faq, index) => (
              <Card key={index}>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">{faq.q}</h3>
                  <p className="text-gray-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* RESUME SUBMISSION MODAL */}
      {showResumeModal && <ResumeModal onClose={() => setShowResumeModal(false)} />}
      
      {/* GENERAL INQUIRY MODAL */}
      {showGeneralInquiry && <GeneralInquiryModal onClose={() => setShowGeneralInquiry(false)} />}

      <Footer />
      
      {/* Fade-up animation */}
      <style jsx>{`
        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-fade-in {
          animation: fadeIn 600ms ease-out;
        }
      `}</style>
    </div>
  );
}

// Resume Submission Modal Component
function ResumeModal({ onClose }) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    desiredRoles: '',
    experience: '',
    consent: false
  });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }
    if (!file) newErrors.file = 'Please upload your resume';
    if (!formData.consent) newErrors.consent = 'You must agree to continue';
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Focus first error field
      const firstError = Object.keys(newErrors)[0];
      document.getElementById(firstError)?.focus();
      return;
    }
    
    // Submit logic here
    console.log('Submitting resume:', formData, file);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <Card className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 flex items-center justify-center mx-auto mb-4">
              <FileCheck className="text-green-600" size={32} />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Thanks!</h3>
            <p className="text-gray-600 mb-6">We'll review your resume and contact you if there's a match.</p>
            <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white">
              Close
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <Card className="max-w-2xl w-full my-8 bg-white" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-8 bg-white">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Submit Your Resume</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="fullName" className="text-gray-700">Full Name *</Label>
              <Input
                id="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                className={`bg-gray-100/50 border-gray-300 ${errors.fullName ? 'border-red-500' : ''}`}
              />
              {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
            </div>
            
            <div>
              <Label htmlFor="email" className="text-gray-700">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className={`bg-gray-100/50 border-gray-300 ${errors.email ? 'border-red-500' : ''}`}
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>
            
            <div>
              <Label htmlFor="phone" className="text-gray-700">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="bg-gray-100/50 border-gray-300"
              />
            </div>
            
            <div>
              <Label htmlFor="location" className="text-gray-700">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="bg-gray-100/50 border-gray-300"
              />
            </div>
            
            <div>
              <Label htmlFor="desiredRoles" className="text-gray-700">Desired roles / keywords</Label>
              <Input
                id="desiredRoles"
                value={formData.desiredRoles}
                onChange={(e) => setFormData({...formData, desiredRoles: e.target.value})}
                className="bg-gray-100/50 border-gray-300"
              />
            </div>
            
            <div>
              <Label htmlFor="experience" className="text-gray-700">Years of experience</Label>
              <select
                id="experience"
                value={formData.experience}
                onChange={(e) => setFormData({...formData, experience: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 bg-gray-100/50 rounded-md"
              >
                <option value="">Select...</option>
                <option value="0-2">0-2 years</option>
                <option value="3-5">3-5 years</option>
                <option value="6-10">6-10 years</option>
                <option value="10+">10+ years</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="file">Upload CV *</Label>
              <div className="mt-2 flex items-center gap-4">
                <label className="flex items-center gap-2 px-4 py-2 border cursor-pointer hover:bg-gray-50">
                  <Upload size={20} />
                  <span>{file ? file.name : 'Choose file'}</span>
                  <input
                    id="file"
                    type="file"
                    accept=".pdf,.docx"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-sm text-gray-500 mt-1">PDF or DOCX, max 10MB</p>
              {errors.file && <p className="text-red-500 text-sm mt-1">{errors.file}</p>}
            </div>
            
            <div className="flex items-start gap-2">
              <Checkbox
                id="consent"
                checked={formData.consent}
                onCheckedChange={(checked) => setFormData({...formData, consent: checked})}
              />
              <label htmlFor="consent" className="text-sm text-gray-600">
                I agree to Symetrix storing my details and contacting me about roles. 
                <a href="#" className="text-blue-600 hover:underline">Privacy policy</a>
              </label>
            </div>
            {errors.consent && <p className="text-red-500 text-sm">{errors.consent}</p>}
            
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
              Submit Resume
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// General Inquiry Modal Component
function GeneralInquiryModal({ onClose }) {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('General inquiry:', formData);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <Card className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="text-green-600" size={32} />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Thanks!</h3>
            <p className="text-gray-600 mb-6">A recruiter will reach out to you soon.</p>
            <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white">
              Close
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="max-w-md w-full bg-white" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-8 bg-white">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Get Career Advice</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-gray-700">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-gray-100/50 border-gray-300"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="inquiry-email" className="text-gray-700">Email</Label>
              <Input
                id="inquiry-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="bg-gray-100/50 border-gray-300"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="message" className="text-gray-700">Message</Label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 bg-gray-100/50 rounded-md"
                required
              />
            </div>
            
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
              Submit Inquiry
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
